import React, { useState } from 'react';
import './MarketplacePracticeLocation.scss';
import { Input } from '@availity/form';
import { FormGroup } from 'reactstrap';
import { EntityLocationInfo } from '../../../../slices/profiles-data-slice/models/entity-location-info';
import {
    LocationType,
    LocationTypeOptions,
} from '../../../../slices/marketplace/timeline/models/enums/location-type.enum';
import Helpers from '../../../../utils/helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../../../Modal/ConfirmationModal';
import StateSelectField from '../../../FormFields/StateSelectField/StateSelectField';
import {
    PracticeFocus,
    PracticeFocusOptionsMapping,
} from '../../../../utils/constants/PracticeFocus.enum';
import { getIn, useFormikContext } from 'formik';

interface MarketplacePracticeLocationProps {
    location: EntityLocationInfo;
    index: number;
    fieldPrefix: string;
    setFormFieldValue(name: string, value: any): void;
    removeLocation(locationIndex: number): void;
    isEditable?: boolean;
    isOtherPracticeFocusColumnVisible?: boolean;
}

const MarketplacePracticeLocation = (
    props: MarketplacePracticeLocationProps
) => {
    const { isEditable = true } = props;
    const location = props.location;
    const [
        isDeleteConfirmationPopupVisible,
        setIsDeleteConfirmationPopupVisible,
    ] = useState(false);
    const { values } = useFormikContext();

    const isOtherPracticeFocusInputVisible = (fieldPath: string) => {
        const selectedFocus = getIn(values, fieldPath);
        return selectedFocus?.toString() === PracticeFocus.Other.toString();
    };
    const handleLocationRemoval = () => {
        props.removeLocation(props.index);
    };

    return (
        <>
            <tr className="practice-location">
                <td className="location-number">
                    <span>{location.locationName}</span>
                </td>
                <td>
                    <FormGroup>
                        <Input
                            name={`${props.fieldPrefix}[${props.index}].addressInfo.streetAddress`}
                            type="text"
                            className="form-control"
                            disabled={!isEditable}
                        />
                    </FormGroup>
                </td>
                <td>
                    <FormGroup>
                        <Input
                            name={`${props.fieldPrefix}[${props.index}].addressInfo.city`}
                            type="text"
                            className="form-control"
                            disabled={!isEditable}
                        />
                    </FormGroup>
                </td>
                <td>
                    <StateSelectField
                        name={`${props.fieldPrefix}[${props.index}].addressInfo.state`}
                        disabled={!isEditable}
                        className=""
                        label=""
                        placeholder="Select State"
                    />
                </td>
                <td>
                    <FormGroup>
                        <Input
                            name={`${props.fieldPrefix}[${props.index}].addressInfo.zipCode`}
                            type="text"
                            className="form-control"
                            disabled={!isEditable}
                            maxLength={5}
                            minLength={5}
                            pattern="[0-9]{5}"
                        />
                    </FormGroup>
                </td>
                <td>
                    <FormGroup>
                        <Input
                            name={`${props.fieldPrefix}[${props.index}].locationType`}
                            type="select"
                            disabled={!isEditable}
                            className="form-control"
                            defaultValue={LocationType.Urban}>
                            {Object.entries(LocationTypeOptions).map(
                                ([key, value]) => (
                                    <option key={key} value={key}>
                                        {value}
                                    </option>
                                )
                            )}
                        </Input>
                    </FormGroup>
                </td>
                <td>
                    <FormGroup>
                        <Input
                            name={`${props.fieldPrefix}[${props.index}].dbaName`}
                            type="text"
                            disabled={!isEditable}
                            className="form-control"
                        />
                    </FormGroup>
                </td>
                <td>
                    <FormGroup>
                        <Input
                            name={`${props.fieldPrefix}[${props.index}].fteDvms`}
                            type="select"
                            className="form-control"
                            disabled={!isEditable}
                            defaultValue={0.5}
                            onChange={(event) => {
                                props.setFormFieldValue(
                                    `${props.fieldPrefix}[${props.index}].fteDvms`,
                                    event.target.value
                                );
                            }}>
                            {Helpers.getFteDvmsNumberList().map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.value}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                </td>
                <td className="practice-focus">
                    <FormGroup>
                        <Input
                            name={`${props.fieldPrefix}[${props.index}].practiceFocusType`}
                            type="select"
                            className="form-control"
                            disabled={!isEditable}>
                            {PracticeFocusOptionsMapping.map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.fullName}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                </td>
                <td hidden={!props.isOtherPracticeFocusColumnVisible}>
                    <FormGroup>
                        <Input
                            hidden={
                                !isOtherPracticeFocusInputVisible(
                                    `${props.fieldPrefix}[${props.index}].practiceFocusType`
                                )
                            }
                            name={`${props.fieldPrefix}[${props.index}].otherPracticeType`}
                            type="text"
                            className="form-control"
                            disabled={!isEditable}
                        />
                    </FormGroup>
                </td>
                <td className="location-number">
                    <FontAwesomeIcon
                        size="lg"
                        icon={faTrash}
                        className={
                            isEditable ? 'delete-icon' : 'delete-icon disabled'
                        }
                        onClick={() =>
                            setIsDeleteConfirmationPopupVisible(true)
                        }
                    />
                </td>
            </tr>
            {isDeleteConfirmationPopupVisible && (
                <ConfirmationModal
                    isVisible={true}
                    setIsVisible={setIsDeleteConfirmationPopupVisible}
                    header={'Location Removal'}
                    confirmationText={`Are you sure you want to remove ${location.locationName}?`}
                    onConfirm={handleLocationRemoval}
                    nextButtonText={'Remove'}
                    cancelButtonText={'Cancel'}
                    params={null}
                />
            )}
        </>
    );
};
export default MarketplacePracticeLocation;
